import { useEffect, useRef, useState } from 'react';
import { HTML_LANG, LANGUAGES, LANGUAGE_NAMES, Language } from '../constants';
import { useA11y, useStrings } from '../i18n';

interface MenuProps {
  lang: Language;
  setLang: (lang: Language) => void;
}

export function Menu({ lang, setLang }: MenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const Strings = useStrings(lang);
  const A11y = useA11y(lang);
  const drawerRef = useRef<HTMLDivElement>(null);
  const scrimRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(false);

  // While the drawer is open, Tab reaches only what is inside it
  useEffect(() => {
    if (!isOpen) return;
    const marked: Element[] = [];
    for (
      let el: HTMLElement | null = drawerRef.current;
      el?.parentElement && el.parentElement !== document.documentElement;
      el = el.parentElement
    ) {
      for (const sib of Array.from(el.parentElement.children)) {
        // the scrim stays live: inert would kill its click-to-close
        if (sib === el || sib === scrimRef.current || sib.hasAttribute('inert')) continue;
        sib.setAttribute('inert', '');
        marked.push(sib);
      }
    }
    return () => marked.forEach((el) => el.removeAttribute('inert'));
  }, [isOpen]);

  // Runs after the effect above, so inert is already off whichever side we focus
  useEffect(() => {
    if (isOpen) closeRef.current?.focus();
    else if (wasOpen.current) triggerRef.current?.focus();
    wasOpen.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  return (
    <>
      {/* Language button */}
      <button
        ref={triggerRef}
        onClick={() => setIsOpen(true)}
        className="w-9 h-9 flex items-center justify-center border border-stone-900 bg-transparent cursor-pointer font-mono text-sm tracking-wide"
        aria-label={`${A11y.selectLanguage}: ${LANGUAGE_NAMES[lang]}`}
      >
        {lang}
      </button>

      {/* Overlay */}
      {isOpen && (
        <div
          ref={scrimRef}
          aria-hidden="true"
          className="fixed inset-0 bg-stone-900/50 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Drawer */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label={A11y.languageMenu}
        inert={!isOpen}
        className={`fixed top-0 right-0 h-full w-64 bg-stone-50 z-50 transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Close button */}
        <button
          ref={closeRef}
          onClick={() => setIsOpen(false)}
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-transparent border-0 cursor-pointer text-2xl"
          aria-label={A11y.closeMenu}
        >
          <span aria-hidden="true">×</span>
        </button>

        <div className="p-6 pt-16">
          <div className="font-mono text-[10px] tracking-[0.16em] uppercase text-stone-600 mb-4">
            {Strings.language}
          </div>
          <div className="flex flex-col gap-2">
            {LANGUAGES.map((l) => (
              <button
                key={l}
                onClick={() => {
                  setLang(l);
                  setIsOpen(false);
                }}
                aria-pressed={l === lang}
                lang={HTML_LANG[l]}
                className={`w-full py-3 px-4 text-left font-serif text-base border cursor-pointer transition-colors ${
                  l === lang
                    ? 'bg-stone-900 text-stone-50 border-stone-900 font-semibold underline decoration-1 underline-offset-4'
                    : 'bg-transparent text-stone-900 border-stone-900/50 hover:border-stone-900'
                }`}
              >
                {LANGUAGE_NAMES[l]}
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
